import {
  deleteObject,
  getMetadata,
  ref,
  updateMetadata,
} from "firebase/storage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { storage } from "senf-shared";
import { getFileTypeLabel } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import { Icon } from "../../..";
import type { UseFileCacheReturn } from "../../../hooks/useFileCache";
import Button from "../../core/button/Button";
import Toast from "../../core/toast/Toast";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import type { ImageInput } from "./ImagesUploadModal";
import {
  getFileIcon,
  sortMediaBrowserFiles,
  THUMBNAIL_CACHE_STORAGE_KEY,
} from "./MediaBrowser";

const selectedFilePreviewStyles = tv({
  base: "relative flex items-center justify-center w-12 h-12 -my-10",
});
const previewImageStyles = tv({
  base: "absolute max-w-12 max-h-12 rounded-sm duration-500",
});
const previewIconContainerStyles = tv({
  base: "flex w-full aspect-square items-center justify-center absolute max-w-10 max-h-10 duration-500 bg-surface-subdued border border-border-subdued rounded",
});
const actionBarStyles = tv({
  base: "absolute bottom-0 translate-y-full left-0 p-5 w-full h-20 z-10 bg-surface-subdued flex justify-between items-center border-border-subdued duration-500 ease-expressive-out",
  variants: {
    visible: {
      true: "border-t translate-y-0",
    },
  },
});

export const deleteStorageFile = async (filePath: string): Promise<boolean> => {
  const fileRef = ref(storage, filePath);
  try {
    await deleteObject(fileRef);
    return true;
  } catch (error) {
    console.error(`Error deleting file at ${filePath}:`, error);
    return false;
  }
};

const unhideStorageFile = async (filePath: string): Promise<boolean> => {
  try {
    const fileRef = ref(storage, filePath);

    const metadata = await getMetadata(fileRef);
    const { hidden, hiddenAt, hiddenBy, ...customMetadata } =
      metadata.customMetadata || {};
    await updateMetadata(fileRef, {
      ...metadata,
      customMetadata,
    });
    return true;
  } catch (error) {
    console.error(`Error unhiding file at ${filePath}:`, error);
    return false;
  }
};

const hideStorageFile = async (
  filePath: string,
  userId: string,
): Promise<boolean> => {
  try {
    const fileRef = ref(storage, filePath);

    // Get existing metadata
    const metadata = await getMetadata(fileRef);

    // Update metadata with hidden flag
    await updateMetadata(fileRef, {
      ...metadata,
      customMetadata: {
        ...metadata.customMetadata,
        hidden: "true",
        hiddenAt: new Date().toISOString(),
        hiddenBy: userId,
      },
    });

    return true;
  } catch (error) {
    console.error(`Error hiding file at ${filePath}:`, error);
    return false;
  }
};
type ActionBarProps = {
  selectedFiles: ImageInput[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<ImageInput[]>>;
  fileTypeLabel: string;
  fileTypes: string[];
  userId: string;
  basePath: string;
  handleError: (error: string) => void;
  onSubmit: (files: ImageInput[]) => void;
  currentImageUrl: string;
  tab: string;
  generatedImage: {
    id: string;
    url: string;
    name: string;
    path: string;
    size: number;
  } | null;
  setGeneratedImage: React.Dispatch<
    React.SetStateAction<{
      id: string;
      url: string;
      name: string;
      path: string;
      size: number;
      width?: number;
      height?: number;
    } | null>
  >;
  fileCacheHook: UseFileCacheReturn;
};
export const ActionBar = ({
  userId,
  basePath,
  handleError,
  onSubmit,
  currentImageUrl,
  tab,
  selectedFiles,
  setSelectedFiles,
  fileTypeLabel,
  fileTypes,
  setGeneratedImage,
  fileCacheHook,
}: ActionBarProps) => {
  const { fileCache, removeFilesFromCache, addFilesToCache } = fileCacheHook;
  const { t } = useTranslation();

  const { openModal } = useModals();
  const [deleteInProgress, setDeleteInProgress] = useState<{
    count: number;
    value: boolean;
  }>({
    count: 0,
    value: false,
  });

  const getLabel = useCallback(
    (n = 1) => {
      return t(getFileTypeLabel(n, fileTypes, fileTypeLabel));
    },
    [fileTypeLabel, fileTypes, t],
  );

  // 6. File operations
  const hideSelectedFiles = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    setDeleteInProgress({
      count: selectedFiles.length,
      value: true,
    }); // We can reuse this state for hiding
    try {
      const failedIds: string[] = [];
      const hiddenIds: string[] = [];

      for (const { url } of selectedFiles) {
        const file = fileCache[basePath]?.find((f) => f.url === url);
        if (!file || file.type === "folder") continue;

        const success = await hideStorageFile(file.id, userId);
        if (success) {
          hiddenIds.push(file.id);
        } else {
          failedIds.push(file.id);
        }
      }

      // Use the hook's removeFilesFromCache function
      if (hiddenIds.length > 0) {
        removeFilesFromCache(basePath, hiddenIds);
      }

      // Clear selection
      setSelectedFiles([]);

      if (failedIds.length > 0) {
        handleError(t("error:hideFailed"));
      }

      hiddenIds.length = 0;
      failedIds.length = 0;
    } catch (err) {
      console.error("Error during file hiding:", err);
      handleError(t("error:hideFailed"));
    } finally {
      setDeleteInProgress((e) => ({
        ...e,
        value: false,
      }));
    }
  }, [
    selectedFiles,
    // fileCache,
    basePath,
    userId,
    handleError,
    t,
    removeFilesFromCache,
  ]);

  // 12. Prepare selected file objects
  const selectedFileObjects = useMemo(() => {
    const cachedFiles = (fileCache[basePath] || []).filter((f) =>
      selectedFiles.some((e) => e.url === f.url),
    );
    if (cachedFiles.length === 0) return selectedFiles;
    return cachedFiles.map(
      (file) =>
        ({
          url: file.url,
          name: file.name,
          size: file.size || 0, // Ensure size is always a number, default to 0
          alt: file.alt || "", // Use name as alt text
        }) as ImageInput,
    );
  }, [fileCache, basePath, selectedFiles]);

  // Update confirmHideFile to include getLabel in dependencies
  const confirmHideFile = useCallback(() => {
    if (selectedFiles.length === 0) return;
    openModal(null, {
      title: t("util:delete", {
        val: getLabel(selectedFiles.length),
      }),
      cancelText: t("global:cancel"),
      submitText: t("global:delete"),
      critical: true,
      zIndex: 9999,
      id: "hideModal",
      onSubmit: () => {
        hideSelectedFiles();
      },
    });
  }, [hideSelectedFiles, t, openModal, selectedFiles.length, getLabel]);

  const handleInsertSelected = useCallback(() => {
    if (selectedFiles.length === 0) return;
    if (tab === "ai") {
      setGeneratedImage((generatedImage) => {
        if (generatedImage) {
          unhideStorageFile(generatedImage.id);
          addFilesToCache(basePath, [generatedImage], sortMediaBrowserFiles);
        }
        return null;
      });
    }
    onSubmit?.(selectedFileObjects);
  }, [
    selectedFileObjects,
    onSubmit,
    selectedFiles.length,
    tab,
    setGeneratedImage,
  ]);

  const handleResetAiGeneratedImage = useCallback(() => {
    setGeneratedImage((generatedImage) => {
      if (generatedImage) deleteStorageFile(generatedImage.id);
      removeFilesFromCache(basePath, [generatedImage?.id]);

      return null;
    });
    setSelectedFiles([]);
  }, [setGeneratedImage, setSelectedFiles]);

  const rotations = useRef<number[]>(
    Array.from(
      { length: 10 },
      (_, i) => (Math.random() - 0.5) * (i % 2 === 0 ? 30 : -30),
    ),
  );

  return (
    <div
      className={actionBarStyles({
        visible: selectedFiles.length > 0,
      })}
    >
      <Toast
        id="deletingMediaFiles"
        leadingIcon={deleteInProgress.value ? "loading" : "checkmark-lg"}
        leadingIconClassName={deleteInProgress.value ? "" : "text-icon-success"}
        show={deleteInProgress.value}
      >
        {deleteInProgress.value
          ? t("util:delete", { val: getLabel(deleteInProgress.count) })
          : t("util:deleted", {
              val: getLabel(deleteInProgress.count),
            })}
      </Toast>
      {tab === "mediaBrowser" && (
        <Button
          leadingIcon="trash"
          critical
          onClick={confirmHideFile}
          disabled={deleteInProgress.value}
        >
          {t("util:delete", {
            val: getLabel(selectedFiles.length),
          })}{" "}
          {selectedFiles.length > 1 ? `(${selectedFiles.length})` : ""}
        </Button>
      )}
      {tab === "ai" && (
        <Button
          leadingIcon="trash"
          critical
          onClick={handleResetAiGeneratedImage}
          disabled={deleteInProgress.value}
        >
          {t("util:discard", {
            val: getLabel(selectedFiles.length),
          })}
        </Button>
      )}

      <div className="flex-1" />

      <div className="flex items-center mr-5">
        <div className={selectedFilePreviewStyles()}>
          {selectedFiles.map((_file, i, arr) => {
            const file =
              Object.values(fileCache)
                .flat()
                .find((f) => f.url === _file.url) || _file;

            if (!file) return null;
            const transform =
              arr.length > 1
                ? `rotate(${rotations.current[i]}deg)`
                : "rotate(0deg)";

            if (
              tab === "stock" ||
              tab === "ai" ||
              file.type.startsWith("image")
            ) {
              return (
                <img
                  key={file.url}
                  src={
                    JSON.parse(
                      window.sessionStorage.getItem(
                        THUMBNAIL_CACHE_STORAGE_KEY,
                      ) || "{}",
                    )[file.url] ||
                    file.thumb ||
                    file.url
                  }
                  alt={file.alt || file.name}
                  className={previewImageStyles({
                    className:
                      (file.type.includes("png") ||
                        file.type.includes("webp")) &&
                      "bg-surface-default p-[1px]",
                  })}
                  style={{ transform }}
                />
              );
            }

            return (
              <div
                key={file.url}
                className={previewIconContainerStyles()}
                style={{ transform }}
              >
                <Icon name={getFileIcon(file.type)} />
              </div>
            );
          })}
        </div>
      </div>

      <Button
        variant="primary"
        onClick={handleInsertSelected}
        className="min-w-[160px]"
        disabled={
          selectedFiles.length === 1 && selectedFiles[0].url === currentImageUrl
        }
        disabledNotice={t("util:added", {
          val: getLabel(1),
        })}
      >
        {currentImageUrl
          ? t("util:swap", { val: getLabel(selectedFiles.length) })
          : t("util:insert", { val: getLabel(selectedFiles.length) })}
      </Button>
    </div>
  );
};
